"use client";

import { motion } from "framer-motion";
import {
  Users,
  CheckCircle,
  Heart,
  Shield,
  TrendingUp,
  Mail,
} from "lucide-react";
import SectionHero from "../components/SectionHero";
import ContentSection from "../components/ContentSection";
import ServiceCard from "../components/ServiceCard";
import { useEffect } from "react";

const CrewManagement = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const services = [
    {
      icon: Users,
      title: "Recruitment & Selection",
      description:
        "Structured approach to sourcing, verifying, and assessing maritime personnel across all ranks and trades.",
      features: [
        "Sourcing through verified channels",
        "Background and document verification",
        "Competence assessment",
        "Matching to vessel type and trade",
      ],
    },
    {
      icon: TrendingUp,
      title: "Planning & Rotation",
      description:
        "Efficient crew planning to balance rest periods, continuity, and operational costs.",
      features: [
        "Long-term crew planning",
        "Rotation planning",
        "Visa and travel logistics",
        "Avoidance of last-minute changes",
      ],
    },
    {
      icon: Heart,
      title: "Payroll & Welfare",
      description:
        "Comprehensive support extending beyond sign-on and sign-off for seafarer wellbeing.",
      features: [
        "Transparent payroll management",
        "Family documentation assistance",
        "Medical and emergency support",
        "Clear communication of policies",
      ],
    },
    {
      icon: Shield,
      title: "Compliance",
      description:
        "Ensuring all crewing practices align with international standards and regulations.",
      features: [
        "International convention compliance",
        "Flag state and port state requirements",
        "Working and rest hours regulations",
        "Company-specific standards",
      ],
    },
  ];

  const crewPositions = [
    { sr: 1, rank: "MASTER", age: "55 YEARS old", wage: 13000 },
    { sr: 2, rank: "CH OFF", age: "40 YEARS old", wage: 9200 },
    { sr: 3, rank: "2ND OFF", age: "35 YEARS old", wage: 4000 },
    { sr: 4, rank: "3RD OFF", age: "30 YEARS old", wage: 1900 },
    { sr: 5, rank: "CH ENG", age: "55 YEARS old", wage: 13000 },
    { sr: 6, rank: "2ND ENG", age: "40 YEARS old", wage: 9200 },
    { sr: 7, rank: "3RD ENG", age: "35 YEARS old", wage: 4000 },
    { sr: 8, rank: "4TH ENG", age: "30 YEARS old", wage: 1900 },
    { sr: 9, rank: "ETO", age: "45 YEARS old", wage: 5600 },
    { sr: 10, rank: "BOSUN", age: "45 YEARS old", wage: 1700 },
    { sr: 11, rank: "PUMPMAN", age: "40 YEARS old", wage: 1600 },
    { sr: 12, rank: "AB", age: "40 YEARS old", wage: 1100 },
    { sr: 13, rank: "OILER", age: "40 YEARS old", wage: 1100 },
    { sr: 14, rank: "OS", age: "30 YEARS old", wage: 300 },
  ];

  return (
    <div className="pt-20">
      <SectionHero
        title="Crew Management"
        subtitle="Right People, Right Skills, Right Time"
        description="Building stable teams on board to reduce turnover, maintain safe operations, and support long-term vessel performance."
      />

      <ContentSection title="Our Crew Management Services">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              features={service.features}
              index={index}
            />
          ))}
        </div>
      </ContentSection>

      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl">
                Why Stable Crews Matter
              </h2>
              <p className="mb-4 text-slate-700">
                A well-managed crew is the foundation of a well-managed ship.
                Stable, familiar teams on board often mean fewer incidents,
                better performance, and improved operational efficiency.
              </p>
              <p className="mb-6 text-slate-700">
                When seafarers feel supported through transparent payroll,
                welfare support, and clear communication, they are more likely
                to stay with the company and take ownership of the vessel's
                performance.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Reduced crew turnover rates
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Improved safety culture
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Better operational performance
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Regulatory compliance assurance
                  </span>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100 p-8"
            >
              <div className="space-y-6">
                <div className="border-b border-teal-200 pb-6">
                  <div className="mb-3 text-3xl font-bold text-teal-600">
                    5000+
                  </div>
                  <p className="text-slate-700">Seafarers in Our Database</p>
                </div>
                <div className="border-b border-teal-200 pb-6">
                  <div className="mb-3 text-3xl font-bold text-teal-600">
                    100%
                  </div>
                  <p className="text-slate-700">
                    Compliance with International Standards
                  </p>
                </div>
                <div>
                  <div className="mb-3 text-3xl font-bold text-teal-600">
                    24/7
                  </div>
                  <p className="text-slate-700">Support & Coordination</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-12 text-center">
              <h2 className="mb-3 text-3xl font-bold text-slate-900 sm:text-4xl">
                FOR: Chemical/Oil Products Tanker
              </h2>

              <div className="flex items-center justify-center mt-4">
                <a
                  href="/contact?subject=Careers"
                  className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-6 py-3 
                 font-semibold text-white shadow-md transition-colors hover:bg-teal-700"
                >
                  <Mail className="h-5 w-5" />
                  Contact Us
                </a>
              </div>
            </div>

            {/* Crew Positions Table */}
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full">
                <thead className="bg-teal-600">
                  <tr>
                    <th className="border-r border-slate-200 px-6 py-4 text-left font-bold text-white">
                      SR. NO.
                    </th>
                    <th className="border-r border-slate-200 px-6 py-4 text-left font-bold text-white">
                      RANK
                    </th>
                    <th className="border-r border-slate-200 px-6 py-4 text-left font-bold text-white">
                      Age is preferably less than
                    </th>
                    <th className="px-6 py-4 text-left font-bold text-white">
                      $ Wages
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {crewPositions.map((position, index) => (
                    <tr
                      key={index}
                      className={`border-t border-slate-200 ${
                        index % 2 === 0 ? "bg-white" : "bg-slate-50"
                      } hover:bg-slate-100 transition-colors`}
                    >
                      <td className="border-r border-slate-200 px-6 py-4 text-slate-900">
                        {position.sr}
                      </td>
                      <td className="border-r border-slate-200 px-6 py-4 font-semibold text-slate-900">
                        {position.rank}
                      </td>
                      <td className="border-r border-slate-200 px-6 py-4 text-slate-700">
                        {position.age}
                      </td>
                      <td className="px-6 py-4 text-slate-900 font-semibold">
                        ${position.wage.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CrewManagement;
